import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import {
    VoiceCallQuestion,
    VoiceCallQuestionListResponse,
    VoiceCallQuestionPatch,
    VoiceCallQuestionStatus,
} from '../models/VoiceAgent'
import { useUser } from './useUser'

const VOICE_AGENT_KEY = 'voice-agent'
const BASE = '/voice-agent'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        if (typeof data.error === 'string' && data.error) {
            return data.error
        }
        const answer = data.answer
        if (Array.isArray(answer) && answer.length > 0) {
            return String(answer[0])
        }
        if (typeof answer === 'string') {
            return answer
        }
    }

    return fallback
}

export interface QuestionListFilters {
    search?: string
    dateFrom?: string
    dateTo?: string
    sort?: 'last_asked' | 'times_asked' | 'created'
}

export const useVoiceAgentQuestions = (
    status?: VoiceCallQuestionStatus,
    page = 1,
    filters: QuestionListFilters = {},
) => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const invalidate = () =>
        queryClient.invalidateQueries({
            queryKey: [VOICE_AGENT_KEY, 'questions'],
        })

    const { data, isLoading, error, refetch } = useQuery<
        VoiceCallQuestionListResponse,
        AxiosError
    >({
        queryKey: [
            VOICE_AGENT_KEY,
            'questions',
            apartmentId,
            status ?? 'all',
            page,
            filters,
        ],
        queryFn: async () => {
            const params: Record<string, string | number> = {
                page,
                page_size: 25,
            }
            if (status) params.status = status
            if (filters.search) params.search = filters.search
            if (filters.dateFrom) params.date_from = filters.dateFrom
            if (filters.dateTo) params.date_to = filters.dateTo
            if (filters.sort) params.sort = filters.sort
            const res = await axiosInstance.get<VoiceCallQuestionListResponse>(
                `${BASE}/questions/`,
                { params },
            )
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnWindowFocus: false,
    })

    const updateQuestion = useMutation<
        VoiceCallQuestion,
        AxiosError,
        { id: number } & VoiceCallQuestionPatch
    >({
        mutationFn: async ({ id, ...body }) => {
            const res = await axiosInstance.patch<VoiceCallQuestion>(
                `${BASE}/questions/${id}/`,
                body,
            )
            return res.data
        },
        onSuccess: (_data, variables) => {
            invalidate()
            if (variables.status === 'dismissed') {
                toast.success('Question dismissed')
            } else if (variables.status === 'pending') {
                toast.success('Question restored')
            } else if (variables.answer) {
                toast.success(
                    'Answer saved. The agent will use it on future calls.',
                )
            } else {
                toast.success('Question updated')
            }
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to update question'))
        },
    })

    return {
        questions: data?.results ?? [],
        count: data?.count ?? 0,
        hasNext: !!data?.next,
        hasPrevious: !!data?.previous,
        pendingCount: data?.pending_count ?? 0,
        counts: {
            pending: data?.pending_count ?? 0,
            answered: data?.answered_count ?? 0,
            dismissed: data?.dismissed_count ?? 0,
        },
        isLoading,
        error,
        refetch,
        updateQuestion,
    }
}
